import NiceModal, { NiceModalHocProps, useModal } from "@ebay/nice-modal-react";
import type { ComponentType } from "react";
import { produce } from "immer";
import { cn } from "@/components/ui/cn";

export interface Filter {
	Name: string;
	Value: string;
}

export interface ListRequest {
	//SearchName?: string;
	Skip: number;
	Take: number;
	SortField?: string;
	SortOrder?: number;
	Filters?: Filter[];
}

export enum ResponseErrorCode {
	GroupWithEmployees = 10,
	GroupNotFound = 11,
	GroupNameRequired = 12,
	GroupDescRequired = 13,
	PhoneExists = 15,
}

export interface BaseResponse {
	Success: boolean;
	ErrorMessage: string;
	ErrorCode: ResponseErrorCode;
}

export interface GenericResponse<TData> extends BaseResponse {
	Data: TData;
}

export interface TestLinkResponse {
	Link?: string;
}

export interface ListResponse<TItem> {
	Items: TItem[],
	TotalRecords: number
}

export function copyWith<T>(base: T, overrides: Partial<T>): T {
	return produce(base, draft => {
		Object.assign(draft as any, overrides);
	});
}

export function withBaseStore<T extends object>(
	initialState: T,
	set: (state: Partial<T & { isHydrated?: boolean }>) => void
) {
	return {
		resetState: () => set(initialState),
		setIsHydrated: (isHydrated: boolean) =>
			set({ isHydrated } as Partial<T & { isHydrated?: boolean }>),
	};
}

// nice-modal-react typed functions. block-start
export interface TypedModalComponent<TProps, TResult = undefined> extends React.FC<TProps & NiceModalHocProps> {
	__modalResultType?: TResult;
}

export function createTypedModal<TProps, TResult = undefined>(
	component: React.FC<TProps & NiceModalHocProps>
): TypedModalComponent<TProps, TResult> {
	return NiceModal.create(component) as TypedModalComponent<TProps, TResult>;
}

type ResolveSignature<T> = undefined extends T
	? (value?: T) => void
	: (value: T) => void;

export function useTypedModal<T = undefined>() {
	const modal = useModal();

	return {
		...modal,
		resolve: ((value?: T) => {
			modal.resolve(value);
		}) as ResolveSignature<T>,
		resolveRemove: ((value?: T) => {
			modal.resolve(value);
			modal.remove();
		}) as ResolveSignature<T>,
	} as typeof modal & {
		resolve: ResolveSignature<T>;
		resolveRemove: ResolveSignature<T>;
	};
}

export function showTypedModal<TComponent extends TypedModalComponent<any, any>>(
	modal: TComponent,
	props: TComponent extends TypedModalComponent<infer P, any> ? P : never,
	id?: string
): Promise<TComponent extends TypedModalComponent<any, infer R> ? R : undefined>;

export function showTypedModal<P, R = undefined>(
	modal: TypedModalComponent<P, R>,
	props: P,
	id?: string
): Promise<R>;

export async function showTypedModal<P, R = undefined>(
	modal: TypedModalComponent<P, R>,
	props: P,
	id?: string
): Promise<R> {
	return await NiceModal.show(modal, { ...props, id: id ?? 'default-modal-id' });
}
// nice-modal-react typed functions. block-end


const wrappedModalCache = new WeakMap<ComponentType<any>, ComponentType<any>>();

/* showTypedModal2 doesn't require you to create additional wrapper for the modal dialog.
*/
export function showTypedModal2<P extends object, TResult = void>(
	modal: ComponentType<P>,
	props: P,
	id?: string
): Promise<TResult> {
	let wrappedModal = wrappedModalCache.get(modal as ComponentType<any>);
	if (!wrappedModal) {
		const created = NiceModal.create(modal as ComponentType<any>);
		wrappedModalCache.set(modal as ComponentType<any>, created);
		wrappedModal = created;
	}
	return NiceModal.show(wrappedModal as any, { ...(props as any), id: id ?? 'default-modal-id' }) as Promise<TResult>;
}

export function getClosestBgClass(el: HTMLElement): string {
	let current: HTMLElement | null = el;
	while (current) {
		const classList = Array.from(current.classList);
		const bgClass = classList.find(className => className.startsWith('bg-') && className !== 'bg-transparent');
		if (bgClass) return bgClass;
		current = current.parentElement;
	}
	// Returning transparent background here leads to broken UI which is hard to debug. Better to make the issue explicit by returning some invalid and easely searchable color . If developer sees it, he will know that need to set magical bg- class somewhere on parent.
	return 'bg-black';
}

export function enumValues<T extends object>(en: T): Array<T[keyof T]> {
	return Object.values(en).filter((v) => typeof v === 'number') as Array<T[keyof T]>;
}

export function formatCurrency(v?: number) {
	if (!v && v !== 0) return "";
	if (!v.toFixed) return v.toString();
	return `₱${v.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

// for possible future use: https://stackoverflow.com/questions/53387613/when-and-how-to-choose-between-react-hooks-and-old-hoc-props-passing
export function reuseComponent<P extends { className?: string }>(
	Component: ComponentType<P>,
	addClassName?: string,
) {
	const Wrapped = (props: P) => (
		<Component {...props} className={cn(addClassName, props.className)} />
	);
	Wrapped.displayName = Component.displayName || Component.name || 'Component';
	return Wrapped;
}

// export interface Country {
// 	name: string;
// 	countryCode: string;
// 	phoneCountryCode?: string;
// }
